import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PersonOutlineOutlined as ProfileIcon,
  PlaceOutlined as AreaIcon,
  BadgeOutlined as HiringIcon,
  BusinessOutlined as BusinessIcon,
  BugReportOutlined as BugIcon,
  AdminPanelSettingsOutlined as AccessIcon,
} from '@mui/icons-material';
import {
  NavPanel, PanelButton, PanelGroup, PanelLink,
} from '../../components/layout/NavPanel';
import { scrollToSection } from '../../components/ui/useScrollSpy';
import { useAuthStore } from '../../store/authStore';

// One panel for the whole Settings section, so the two screens under it do
// not drift apart.
//
// The rows for Profile and Service areas are anchors into one screen, not
// routes — they used to be two PanelLinks both pointing at /settings with
// `exact`, so both matched and both rendered selected at once. `anchor` is
// whichever section you are actually looking at, from a scroll spy; on the
// Bug reports screen it is simply absent and neither is selected.

const SettingsPanel: React.FC<{ anchor?: string | null }> = ({ anchor }) => {
  const { pathname } = useLocation();
  const { account, canArea } = useAuthStore();
  const isSystemAdmin = account?.role === 'SA';
  const canEditCompany = canArea('companySettings', 'view');
  const onSettingsRoot = pathname === '/settings';
  const navigate = useNavigate();
  // The anchors live on /settings. From another Settings page (Access, Bug
  // reports) they go there first — they used to do nothing at all.
  const goTo = (id: string) => (onSettingsRoot ? scrollToSection(id) : navigate(`/settings#${id}`));

  return (
    <NavPanel>
      <PanelGroup label="You">
        <PanelButton
          icon={<ProfileIcon />}
          label="Profile"
          active={onSettingsRoot && anchor === 'profile'}
          onClick={() => goTo('profile')}
        />
      </PanelGroup>

      <PanelGroup label="This company">
        {canEditCompany && (
          <PanelButton
            icon={<BusinessIcon />}
            label="Company"
            active={onSettingsRoot && anchor === 'company'}
            onClick={() => goTo('company')}
          />
        )}
        <PanelButton
          icon={<AreaIcon />}
          label="Service areas"
          active={onSettingsRoot && anchor === 'service-areas'}
          onClick={() => goTo('service-areas')}
        />
        <PanelButton
          icon={<HiringIcon />}
          label="Hiring"
          active={onSettingsRoot && anchor === 'hiring'}
          onClick={() => goTo('hiring')}
        />
        {canArea('access', 'manage') && (
          <PanelLink to="/settings/access" icon={<AccessIcon />} label="Access" currentPath={pathname} />
        )}
      </PanelGroup>

      {isSystemAdmin && (
        <PanelGroup label="System">
          {/* This screen used to carry a second Companies list — read plus
              delete, with its own confirm dialog — alongside the /companies
              page that already does list, create, edit and delete. Two ways
              to delete a tenant, wearing different warnings, is not a
              styling problem. The card is gone; this is the link. */}
          <PanelLink to="/companies" icon={<BusinessIcon />} label="Companies" currentPath={pathname} />
          <PanelLink to="/settings/bug-reports" icon={<BugIcon />} label="Bug reports" currentPath={pathname} />
        </PanelGroup>
      )}
    </NavPanel>
  );
};

export default SettingsPanel;
